// Client-side Discover ranking — a faithful port of the backend rankDiscover
// scoring (backend/pkg/api/tokens/ranking.go). The backend already selected and
// filtered the rows (liquidity floor, stablecoin exclusion, metadata gate); this
// re-ranks that SAME set live as WS trades tick the values, so a token heating up
// visibly climbs positions in real time. Because the formulas are set-independent
// and identical to the server's, the live order matches the next REST reload with
// zero divergence (no jump).
//
// Keep these formulas byte-for-byte in sync with ranking.go.

import type { TokensSort } from '../api/tokens'
import type { TokenSummary } from '../api/types'

// Top composite (must match ranking.go topScore).
const TOP_W_VOLUME = 0.55
const TOP_W_LIQUIDITY = 0.15
const TOP_W_TXNS = 0.3
const TOP_REF_LOG_VOLUME = 6.2
const TOP_REF_LOG_TXNS = 3.0
const TOP_REF_LOG_LIQUIDITY = 6.0

function logSafe(x: number): number {
  if (!(x > 0) || Number.isNaN(x)) return 0
  return Math.log10(1 + x)
}
function clamp01(x: number): number {
  if (x < 0 || Number.isNaN(x)) return 0
  return x > 1 ? 1 : x
}
function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

// topScore — absolute-size composite, activity-led (volume + txns = 85%),
// liquidity a 15% nudge. Set-independent fixed-reference log normalisation.
// Volume is USD at each trade's own time (volume_window_usd, priced trades
// only), so pools of different quotes rank in one basis.
export function topScore(volUsd: number, txns: number, liqUsd: number | null | undefined): number {
  const v = clamp01(logSafe(num(volUsd)) / TOP_REF_LOG_VOLUME)
  const t = clamp01(logSafe(num(txns)) / TOP_REF_LOG_TXNS)
  const l = clamp01(logSafe(num(liqUsd)) / TOP_REF_LOG_LIQUIDITY)
  return TOP_W_VOLUME * v + TOP_W_LIQUIDITY * l + TOP_W_TXNS * t
}

// trendingScore — volume acceleration recent^1.5 / window, both in USD at each
// trade's own time. Size-independent recent SHARE nudged by sqrt(recent) so a
// lone tiny trade can't top it.
export function trendingScore(recentUsd: number, windowUsd: number): number {
  const r = num(recentUsd)
  const w = num(windowUsd)
  if (w <= 0 || r <= 0) return 0
  return (r * Math.sqrt(r)) / w
}

// surgeScore — price GAIN confirmed by trading: gain × (txns-1)/txns. Gain leads;
// a single-trade print scores 0 (a wick, not a surge).
export function surgeScore(pricePct: number | null | undefined, txns: number): number {
  const p = typeof pricePct === 'number' && Number.isFinite(pricePct) ? pricePct : 0
  if (p <= 0 || txns <= 1) return 0
  return p * ((txns - 1) / txns)
}

function categoryScore(t: TokenSummary, sort: TokensSort): number {
  switch (sort) {
    case 'top':
      return topScore(num(t.volume_window_usd), num(t.txns_24h), t.liquidity_usd)
    case 'trending':
      return trendingScore(num(t.vol_recent_usd), num(t.volume_window_usd))
    case 'surge':
      return surgeScore(t.price_change_24h_pct, num(t.txns_24h))
    default:
      return 0
  }
}

// rankDiscoverRows returns the rows re-ordered by the tab's own scoring (best
// first), or unchanged for 'new' (server age order) and the legacy keys. Stable:
// equal scores keep the incoming order, so the list doesn't jitter.
export function rankDiscoverRows(rows: TokenSummary[], sort: TokensSort): TokenSummary[] {
  if (sort !== 'top' && sort !== 'trending' && sort !== 'surge') return rows
  return rows
    .map((row, index) => ({ row, index, score: categoryScore(row, sort) }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ row }) => row)
}

export function rankDiscoverCandidateRows(rows: TokenSummary[], sort: TokensSort): TokenSummary[] {
  if (sort === 'top' || sort === 'trending' || sort === 'surge') {
    return rankDiscoverRows(rows, sort)
  }
  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      let order = 0
      if (sort === 'volume_24h')
        order = num(right.row.volume_window_usd) - num(left.row.volume_window_usd)
      if (sort === 'txns_24h') order = right.row.txns_24h - left.row.txns_24h
      if (sort === 'new') order = tokenAge(right.row) - tokenAge(left.row)
      return order || left.index - right.index
    })
    .map(({ row }) => row)
}

function tokenAge(row: TokenSummary): number {
  const parsed = row.discovered_at ? Date.parse(row.discovered_at) : 0
  return Number.isFinite(parsed) ? parsed : 0
}
